import { useState, useMemo, useRef } from 'react';

// Shared brand-derived sequential ramp (from tailwind.config.js `brand` scale).
// Used for single-series magnitude charts — never mixed with a categorical set.
const BRAND = {
  100: '#f5ebe4',
  300: '#d0b3a1',
  500: '#a37156',
  600: '#945e46',
  700: '#7c4b38',
};

const INK = {
  primary: '#1c1917', // stone-900
  secondary: '#57534e', // stone-600
  muted: '#a8a29e', // stone-400
  grid: '#e7e5e4', // stone-200
  surface: '#ffffff',
};

interface TrendPoint {
  label: string;
  value: number;
}

interface TrendChartProps {
  title: string;
  subtitle?: string;
  data: TrendPoint[];
  formatValue: (v: number) => string;
  variant?: 'area' | 'bar';
}

const CHART_W = 520;
const CHART_H = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 8 };

export function TrendChart({ title, subtitle, data, formatValue, variant = 'area' }: TrendChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;

  const maxVal = Math.max(1, ...data.map(d => d.value));
  const yMax = maxVal * 1.15;

  const xStep = data.length > 1 ? plotW / (data.length - 1) : plotW;
  const yFor = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const xFor = (i: number) => PAD.left + (data.length > 1 ? i * xStep : plotW / 2);

  const points = data.map((d, i) => ({ x: xFor(i), y: yFor(d.value), ...d }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = points.length > 0
    ? `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${PAD.top + plotH} L ${points[0].x.toFixed(1)} ${PAD.top + plotH} Z`
    : '';

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(f => PAD.top + plotH * (1 - f));

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || data.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * CHART_W;
    let nearest = 0;
    let best = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - relX);
      if (d < best) { best = d; nearest = i; }
    });
    setHoverIdx(nearest);
  };

  const hovered = hoverIdx !== null ? points[hoverIdx] : null;

  return (
    <div className="bg-white border border-stone-200/80 rounded-xl shadow-xs p-4">
      <div className="flex items-baseline justify-between mb-1">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-stone-700">{title}</h4>
        {subtitle && <span className="text-[9px] text-stone-400 font-semibold">{subtitle}</span>}
      </div>
      {data.length === 0 ? (
        <div className="h-[160px] flex items-center justify-center text-xs text-stone-400 italic">Bu dönem için veri yok.</div>
      ) : (
        <div className="relative">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            className="w-full h-auto"
            onMouseMove={handleMove}
            onMouseLeave={() => setHoverIdx(null)}
            role="img"
            aria-label={title}
          >
            {gridLines.map((y, i) => (
              <line key={i} x1={PAD.left} x2={CHART_W - PAD.right} y1={y} y2={y} stroke={INK.grid} strokeWidth={1} />
            ))}

            {variant === 'bar' ? (
              points.map((p, i) => {
                const bw = Math.min(24, xStep * 0.5);
                return (
                  <rect
                    key={i}
                    x={p.x - bw / 2}
                    y={p.y}
                    width={bw}
                    height={Math.max(0, PAD.top + plotH - p.y)}
                    rx={4}
                    fill={hoverIdx === i ? BRAND[600] : BRAND[500]}
                  />
                );
              })
            ) : (
              <>
                <path d={areaPath} fill={BRAND[500]} opacity={0.1} />
                <path d={linePath} fill="none" stroke={BRAND[600]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              </>
            )}

            {points.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={hoverIdx === i ? 5 : 4}
                fill={BRAND[700]}
                stroke={INK.surface}
                strokeWidth={2}
                opacity={variant === 'bar' ? (hoverIdx === i ? 1 : 0) : 1}
              />
            ))}

            {hovered && (
              <line x1={hovered.x} x2={hovered.x} y1={PAD.top} y2={PAD.top + plotH} stroke={INK.muted} strokeWidth={1} strokeDasharray="2 2" />
            )}

            {data.map((d, i) => {
              const showEvery = data.length > 8 ? 2 : 1;
              if (i % showEvery !== 0 && i !== data.length - 1) return null;
              return (
                <text key={i} x={xFor(i)} y={CHART_H - 6} textAnchor="middle" fontSize={9} fontWeight={600} fill={INK.muted}>
                  {d.label}
                </text>
              );
            })}
          </svg>

          {hovered && (
            <div
              className="absolute pointer-events-none bg-stone-900 text-white text-[10px] font-bold rounded-lg px-2.5 py-1.5 shadow-lg -translate-x-1/2 -translate-y-full"
              style={{
                left: `${(hovered.x / CHART_W) * 100}%`,
                top: `${(hovered.y / CHART_H) * 100}%`,
                marginTop: '-8px',
              }}
            >
              <div className="text-stone-300 font-semibold text-[9px]">{hovered.label}</div>
              <div>{formatValue(hovered.value)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface DesignerRankItem {
  id: string;
  name: string;
  value: number;
}

interface DesignerRankBarProps {
  items: DesignerRankItem[];
  formatValue: (v: number) => string;
  maxItems?: number;
}

export function DesignerRankBar({ items, formatValue, maxItems = 8 }: DesignerRankBarProps) {
  const [hoverId, setHoverId] = useState<string | null>(null);

  const ranked = useMemo(
    () => [...items].filter(i => i.value > 0).sort((a, b) => b.value - a.value).slice(0, maxItems),
    [items, maxItems]
  );

  const maxVal = Math.max(1, ...ranked.map(r => r.value));

  if (ranked.length === 0) {
    return (
      <div className="bg-white border border-stone-200/80 rounded-xl shadow-xs p-4">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-stone-700 mb-3">Tasarımcı Sıralaması (Hacim)</h4>
        <div className="h-[120px] flex items-center justify-center text-xs text-stone-400 italic">Bu dönem için veri yok.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200/80 rounded-xl shadow-xs p-4">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-stone-700 mb-3">Tasarımcı Sıralaması (Hacim)</h4>
      <div className="space-y-2.5">
        {ranked.map((r, i) => {
          const pct = (r.value / maxVal) * 100;
          const isTop = i === 0;
          return (
            <div
              key={r.id}
              className="group"
              onMouseEnter={() => setHoverId(r.id)}
              onMouseLeave={() => setHoverId(null)}
            >
              <div className="flex items-center justify-between text-[10px] mb-0.5">
                <span className={`font-bold truncate max-w-[60%] ${isTop ? 'text-brand-700' : 'text-stone-700'}`}>
                  {i + 1}. {r.name}
                </span>
                <span className={`font-mono font-extrabold transition-colors ${hoverId === r.id ? 'text-brand-700' : 'text-stone-500'}`}>
                  {formatValue(r.value)}
                </span>
              </div>
              <div className="h-2.5 w-full bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(3, pct)}%`,
                    backgroundColor: hoverId === r.id ? BRAND[700] : (isTop ? BRAND[600] : BRAND[500]),
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
